import {
  Dropdown,
  Field,
  makeStyles,
  Option,
  SpinButton,
  Switch,
  tokens,
} from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import { Section, SectionColumn, TextEdit } from '@powerportalspro/react-fluent';
import { useMemo, useState } from 'react';

// Localization key namespace — reuses the keys the Blazor demo's
// `IStringLocalizer<TextEditPropertiesDemo>` resolves to, so both stacks
// read from the same JSON instead of maintaining parallel copies. The
// framework's bundle service flattens keys under `components.<FullTypeName>.`
// (matching Blazor's `IStringLocalizer<T>` behavior), so the prefix is the
// C# type's full namespace — not the source JSON's nesting (`app.Pages.…`).
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Editors.TextEdit.TextEditPropertiesDemo';

// HTML <input type="..."> values that the React TextEdit accepts. Lowercase
// because the underlying attribute requires it.
const TYPE_VALUES = ['text', 'email', 'tel', 'url', 'search', 'password'] as const;
type TextEditType = (typeof TYPE_VALUES)[number];

const useStyles = makeStyles({
  // Horizontal Field orientation puts labels inline with their inputs, so the
  // toolbar's controls now align by baseline rather than by tops-of-stacks —
  // 'center' keeps Switches (no label-above offset) and Fields (with inline
  // labels) on the same visual row.
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  // Applied to each horizontal-orientation Field. Three effects:
  // 1. `flexShrink: 0` keeps the Field at its natural content width so the
  //    label + input stay paired. Without it, narrow viewports shrink the
  //    Field and the label text wraps to multiple lines next to a still-wide
  //    input. With it, the entire Field stays one row tall and the toolbar's
  //    `flexWrap: wrap` moves overflowed Fields to the next row instead.
  // 2. `whiteSpace: nowrap` on the inner label keeps multi-word labels
  //    (e.g. "Min Length") on a single line. Reinforces #1 — together they
  //    guarantee the Field can't internally rewrap.
  // 3. `gridTemplateColumns: auto auto` overrides Field's default `33% 1fr`
  //    layout, which sizes the label column to 33% of the field's total
  //    width — too narrow for "Min Length" next to an 85px input, causing
  //    the label to overflow into the input column. `auto auto` lets each
  //    column size to its content (label text + input width respectively).
  inlineField: {
    flexShrink: 0,
    gridTemplateColumns: 'auto auto',
    '& label': {
      whiteSpace: 'nowrap',
    },
  },
  numberInput: {
    width: '85px',
  },
  typeInput: {
    minWidth: '125px',
  },
});

export function TextEditPropertiesSample() {
  const styles = useStyles();
  const t = useT();
  const [readOnly, setReadOnly] = useState(false);
  const [required, setRequired] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [minLength, setMinLength] = useState(3);
  const [maxLength, setMaxLength] = useState(10);
  const [type, setType] = useState<TextEditType>('text');

  // Memoized so the labels re-resolve when the active locale changes.
  // Capitalization (`text` → `Text`) matches the Blazor demo's
  // `textFieldType.{nameof(TextFieldType.Text)}` keying.
  const typeOptions = useMemo(
    () =>
      TYPE_VALUES.map((value) => ({
        value,
        label: t(
          `${STRINGS_BASE}.textFieldType.${value.charAt(0).toUpperCase()}${value.slice(1)}`,
        ),
      })),
    [t],
  );

  return (
    <Section>
      <SectionColumn
        header={
          <div className={styles.toolbar}>
            <Switch
              label={t(`${STRINGS_BASE}.readonly`)}
              labelPosition="before"
              checked={readOnly}
              onChange={(_, d) => setReadOnly(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.required`)}
              labelPosition="before"
              checked={required}
              onChange={(_, d) => setRequired(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.disabled`)}
              labelPosition="before"
              checked={disabled}
              onChange={(_, d) => setDisabled(d.checked)}
            />
            <Field
              label={t(`${STRINGS_BASE}.min-length`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <SpinButton
                className={styles.numberInput}
                min={2}
                value={minLength}
                onChange={(_, d) => {
                  const n = typeof d.value === 'number' ? d.value : Number(d.displayValue);
                  if (Number.isFinite(n)) setMinLength(n);
                }}
              />
            </Field>
            <Field
              label={t(`${STRINGS_BASE}.max-length`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <SpinButton
                className={styles.numberInput}
                value={maxLength}
                onChange={(_, d) => {
                  const n = typeof d.value === 'number' ? d.value : Number(d.displayValue);
                  if (Number.isFinite(n)) setMaxLength(n);
                }}
              />
            </Field>
            <Field
              label={t(`${STRINGS_BASE}.text-field-type`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.typeInput}
                value={typeOptions.find((o) => o.value === type)?.label ?? type}
                selectedOptions={[type]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setType(d.optionValue as TextEditType);
                }}
              >
                {typeOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </div>
        }
      >
        <TextEdit
          columnName="ppp_mobilephone"
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          minLength={minLength}
          maxLength={maxLength}
          type={type}
        />
      </SectionColumn>
    </Section>
  );
}
